import { useEffect } from 'react'
import { useAuth } from '@/contexts/AuthProvider'

/**
 * Dynamically updates PWA-related meta tags and document title
 * based on company's mobile_app_name and mobile_app_icon_url settings.
 * These affect the PWA install name/icon, not the in-app header.
 */
export default function PWAMetaUpdater() {
  const { company } = useAuth()

  useEffect(() => {
    if (!company) return

    const appName = company.mobile_app_name || 'AI Training Matrix'
    const appIcon = company.mobile_app_icon_url

    // Update document title (shown in browser tab and PWA title bar)
    document.title = appName

    // Update apple-mobile-web-app-title
    const appleTitleMeta = document.querySelector('meta[name="apple-mobile-web-app-title"]')
    if (appleTitleMeta) appleTitleMeta.setAttribute('content', appName)

    // Update apple-touch-icon if custom icon is set
    if (appIcon) {
      const appleTouchIcon = document.querySelector('link[rel="apple-touch-icon"]')
      if (appleTouchIcon) appleTouchIcon.setAttribute('href', appIcon)

      const favicon = document.querySelector('link[rel="icon"]')
      if (favicon) favicon.setAttribute('href', appIcon)
    }

    // Generate a dynamic manifest blob to override the static one
    const existingManifestLink = document.querySelector('link[rel="manifest"]')
    if (existingManifestLink) {
      const icons = appIcon
        ? [
            { src: appIcon, sizes: '192x192', type: 'image/png', purpose: 'any' },
            { src: appIcon, sizes: '512x512', type: 'image/png', purpose: 'any' },
            { src: appIcon, sizes: '512x512', type: 'image/png', purpose: 'maskable' },
          ]
        : [
            { src: 'https://storage.googleapis.com/trainingmatrix/images/app-icon-192.png', sizes: '192x192', type: 'image/png', purpose: 'any' },
            { src: 'https://storage.googleapis.com/trainingmatrix/images/app-icon-512.png', sizes: '512x512', type: 'image/png', purpose: 'any' },
            { src: 'https://storage.googleapis.com/trainingmatrix/images/app-icon-512.png', sizes: '512x512', type: 'image/png', purpose: 'maskable' },
          ]

      const manifest = {
        id: '/',
        name: appName,
        short_name: appName.length > 12 ? appName.substring(0, 12) : appName,
        description: 'Mobile training profile and compliance app',
        theme_color: '#059669',
        background_color: '#ffffff',
        display: 'standalone',
        orientation: 'portrait-primary',
        start_url: '/',
        scope: '/',
        categories: ['business', 'productivity'],
        icons,
      }

      const blob = new Blob([JSON.stringify(manifest)], { type: 'application/json' })
      const manifestUrl = URL.createObjectURL(blob)
      existingManifestLink.setAttribute('href', manifestUrl)

      return () => URL.revokeObjectURL(manifestUrl)
    }
  }, [company])

  return null
}
